export type CollateralStaffReferenceInput = {
  id: number | string;
  collateralType?: string | null;
  buildingSubType?: string | null;
  landTitleNumber?: string | null;
  documentNumber?: string | null;
  ownerName?: string | null;
};

export type CollateralStaffReference = {
  /** Lao-first primary label for staff-facing cards, titles, and pickers. */
  primaryLabel: string;
  /** System ID preserved for traceability; never use this as a selection value replacement. */
  systemIdLabel: string;
  /** Search terms for an already-authorized, in-memory collateral collection only. */
  searchableText: string;
  /** Whether a recorded document identifier, rather than only the system ID, is shown first. */
  hasDocumentReference: boolean;
};

function readTrimmed(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function isLandOrBuilding(collateralType: string | null | undefined): boolean {
  return ["ທີ່ດິນ", "land", "ເຮືອນ", "ເຮືອນ/ອາຄານ", "building", "house"]
    .includes(readTrimmed(collateralType)?.toLowerCase() ?? "");
}

/**
 * Presentation-only reference for staff communication.
 *
 * The database/API/audit identity is always the collateral ID. Document numbers
 * are neither unique nor mandatory, so the system ID remains available beside
 * any document-first label and in searchable text.
 */
export function getCollateralStaffReference(input: CollateralStaffReferenceInput): CollateralStaffReference {
  const collateralType = readTrimmed(input.collateralType);
  const ownerName = readTrimmed(input.ownerName);
  const landTitleNumber = readTrimmed(input.landTitleNumber);
  const documentNumber = readTrimmed(input.documentNumber);
  const systemIdLabel = `ລະຫັດລະບົບ #${String(input.id)}`;
  const landOrBuilding = isLandOrBuilding(collateralType);

  const primaryLabel = landOrBuilding && landTitleNumber
    ? `ເລກທີ່ໃບຕາດິນ: ${landTitleNumber}`
    : documentNumber
      ? `ເລກທີເອກະສານ: ${documentNumber}`
      : `ຫຼັກຊັບ #${String(input.id)}`;

  return {
    primaryLabel,
    systemIdLabel,
    searchableText: [
      primaryLabel,
      systemIdLabel,
      landTitleNumber,
      documentNumber,
      collateralType,
      ownerName,
    ].filter(Boolean).join(" "),
    hasDocumentReference: Boolean((landOrBuilding && landTitleNumber) || documentNumber),
  };
}
